import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  ExternalLink,
  MapPin,
  Star,
  Package,
  Phone,
  FileText,
  Target,
  Hash,
  Users,
  Mail,
  CheckCircle,
  XCircle,
} from "lucide-react";
import {
  PpcFarmTargetLedgerData,
  PpcFarmCohortLedgerData,
  PpcFarmMailingLedgerData,
} from "../../types/ppcFarmTypes";
import { Button } from "../ui/button";

// Generic ledger data type
type GenericLedgerData =
  | PpcFarmTargetLedgerData
  | PpcFarmCohortLedgerData
  | PpcFarmMailingLedgerData;

interface GenericLedgerDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: GenericLedgerData | null;
  ledgerType: "target" | "cohort" | "mailing";
}

const GenericLedgerDetailModal: React.FC<GenericLedgerDetailModalProps> = ({
  isOpen,
  onClose,
  data,
  ledgerType,
}) => {
  if (!data) return null;

  const formatDate = (timestamp: any): string => {
    if (!timestamp) return "—";

    try {
      let date: Date;

      // Handle Firestore Timestamp objects (client-side SDK)
      if (timestamp && typeof timestamp === "object" && timestamp.toDate) {
        date = timestamp.toDate();
      }
      // Handle plain Date objects
      else if (timestamp instanceof Date) {
        date = timestamp;
      }
      // Handle timestamp numbers (milliseconds)
      else if (typeof timestamp === "number") {
        date = new Date(timestamp);
      }
      // Handle timestamp strings
      else if (typeof timestamp === "string") {
        date = new Date(timestamp);
      }
      // Handle serialized Firestore timestamps from Netlify function (server-side SDK)
      else if (
        timestamp &&
        typeof timestamp === "object" &&
        typeof timestamp.seconds === "number"
      ) {
        // Convert seconds to milliseconds and add nanoseconds if present
        const milliseconds =
          timestamp.seconds * 1000 +
          Math.floor((timestamp.nanoseconds || 0) / 1000000);
        date = new Date(milliseconds);
      } else {
        console.warn("Unknown timestamp format in modal:", timestamp);
        return "—";
      }

      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.warn("Invalid date created in modal from:", timestamp);
        return "—";
      }

      // Format as long date
      return date.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    } catch (error) {
      console.error(
        "Error formatting date in modal:",
        error,
        "Input:",
        timestamp,
      );
      return "—";
    }
  };

  const formatValue = (value: any): string => {
    if (value === null || value === undefined || value === "") {
      return "—";
    }
    return String(value);
  };

  const formatRating = (rating: number | null): string => {
    if (rating === null || rating === undefined) return "—";
    return `${rating}/5`;
  };

  const formatNumber = (num: number | null): string => {
    if (num === null || num === undefined) return "—";
    return num.toLocaleString();
  };

  const formatBoolean = (value: boolean | null): React.ReactNode => {
    if (value === null || value === undefined) return "—";
    return (
      <div className="flex items-center gap-1">
        {value ? (
          <CheckCircle className="h-3 w-3 text-green-500" />
        ) : (
          <XCircle className="h-3 w-3 text-red-500" />
        )}
        <span>{value ? "Yes" : "No"}</span>
      </div>
    );
  };


  const handleExternalLinkClick = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const getModalTitle = (): string => {
    switch (ledgerType) {
      case "target":
        return (
          (data as PpcFarmTargetLedgerData).sellerName || "Target Ledger Entry"
        );
      case "cohort":
        // cohortNumber already contains "Cohort X", so don't add "Cohort" prefix
        return (
          (data as PpcFarmCohortLedgerData).cohortNumber ||
          "Cohort Ledger Entry"
        );
      case "mailing":
        const mailingData = data as PpcFarmMailingLedgerData;
        return mailingData.mailerId
          ? `Mailer ${mailingData.mailerId}`
          : "Mailing Ledger Entry";
      default:
        return "Ledger Entry";
    }
  };

  const getModalDescription = (): string => {
    switch (ledgerType) {
      case "target":
        const targetData = data as PpcFarmTargetLedgerData;
        return `${targetData.cohort} • Added by ${targetData.addedBy}`;
      case "cohort":
        const cohortData = data as PpcFarmCohortLedgerData;
        return `Created by ${cohortData.yourName} • Count: ${cohortData.count}`;
      case "mailing":
        const mailingData = data as PpcFarmMailingLedgerData;
        return `For ${mailingData.cohort || "No Cohort"} • Entered by ${mailingData.name}`;
      default:
        return "";
    }
  };

  const renderTargetLedgerContent = (targetData: PpcFarmTargetLedgerData) => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
      {/* Location Information */}
      <Card className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-md">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base text-gray-900 dark:text-white">
            <MapPin className="h-4 w-4 text-green-500 dark:text-green-400" />
            Location Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div>
            <span className="text-gray-600 dark:text-gray-400 block">
              Full Address:
            </span>
            <span className="text-gray-900 dark:text-white">
              {formatValue(targetData.fullAddress)}
            </span>
          </div>
          {targetData.sellerPhone && (
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Phone:</span>
              <span className="text-gray-900 dark:text-white flex items-center gap-1">
                <Phone className="h-3 w-3 text-green-500 dark:text-green-400" />
                {formatValue(targetData.sellerPhone)}
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Performance Metrics */}
      <Card className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-md">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base text-gray-900 dark:text-white">
            <Star className="h-4 w-4 text-yellow-500 dark:text-yellow-400" />
            Performance Metrics
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">
              Star Rating:
            </span>
            <span className="text-gray-900 dark:text-white">
              {formatRating(targetData.starRating)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">
              Total Ratings:
            </span>
            <span className="text-gray-900 dark:text-white">
              {formatNumber(targetData.totalRatings)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">Products:</span>
            <span className="text-gray-900 dark:text-white">
              {formatNumber(targetData.productCount)}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Additional Information */}
      <Card className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-md">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base text-gray-900 dark:text-white">
            <FileText className="h-4 w-4 text-indigo-500 dark:text-indigo-400" />
            Additional Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          {targetData.sellerDescription && (
            <div>
              <span className="text-gray-600 dark:text-gray-400 block">
                Description:
              </span>
              <span className="text-gray-900 dark:text-white">
                {formatValue(targetData.sellerDescription)}
              </span>
            </div>
          )}
          {targetData.otherBrands && (
            <div>
              <span className="text-gray-600 dark:text-gray-400 block">
                Other Brands:
              </span>
              <span className="text-gray-900 dark:text-white">
                {formatValue(targetData.otherBrands)}
              </span>
            </div>
          )}
          {targetData.sellerPageLink && (
            <div>
              <span className="text-gray-600 dark:text-gray-400 block mb-2">
                Seller Page:
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  handleExternalLinkClick(targetData.sellerPageLink!)
                }
                className="flex items-center gap-2 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 h-8 text-xs"
              >
                <ExternalLink className="h-3 w-3" />
                View Seller Page
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  // Helper function to humanize filter badge labels (same as in main component)
  const humanizeFilterLabel = (
    key: string,
    value: any,
  ): { label: string; displayValue?: string } => {
    const labelMap: Record<string, string> = {
      minOfProducts: "Min Products",
      maxOfProducts: "Max Products",
      minOfRatings: "Min Ratings",
      maxOfRatings: "Max Ratings",
      minOfOtherBrands: "Min Brands",
      maxOfOtherBrands: "Max Brands",
      minOfBrands: "Min Brands",
      maxOfBrands: "Max Brands",
      isUsSellers: "US Sellers",
      isUsProducts: "US Products",
      sunPosition: "Sun Position",
      totalCount: "Total Count",
      // Add more mappings as needed
    };

    // Special handling for isUsSellers to ensure it displays as "US Sellers"
    if (key === "isUsSellers" && value === true) {
      return { label: "US Sellers" };
    }

    const label =
      labelMap[key] ||
      key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase());

    // For boolean true values, only show the label
    if (typeof value === "boolean" && value === true) {
      return { label };
    }

    // For other values, show Label: Value
    return {
      label,
      displayValue: typeof value === "number" ? value.toString() : value,
    };
  };

  const renderCohortLedgerContent = (cohortData: PpcFarmCohortLedgerData) => (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-4">
      {/* Left Column - Main Information Panel (wider, 60-70% space) */}
      <div className="lg:col-span-2 space-y-4">
        {/* Performance Metrics */}
        <Card className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-md">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base text-gray-900 dark:text-white">
              <Star className="h-4 w-4 text-yellow-500 dark:text-yellow-400" />
              Performance Metrics
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">
                Discovery Calls:
              </span>
              <span className="text-gray-900 dark:text-white">
                {formatNumber(cohortData.discoveryCall)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">
                Signed Clients:
              </span>
              <span className="text-gray-900 dark:text-white">
                {formatNumber(cohortData.signedClients)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">
                Conversion Rate:
              </span>
              <span className="text-gray-900 dark:text-white">
                {formatNumber(cohortData.conversionRateSignedClients)}%
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Additional Information */}
        <Card className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-md">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base text-gray-900 dark:text-white">
              <FileText className="h-4 w-4 text-indigo-500 dark:text-indigo-400" />
              Additional Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {cohortData.isTriedAndTrue && (
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span className="text-gray-900 dark:text-white">
                  Tried & True Cohort
                </span>
              </div>
            )}
            {cohortData.mailerShortDescription && (
              <div>
                <span className="text-gray-600 dark:text-gray-400 block">
                  Mailer Description:
                </span>
                <span className="text-gray-900 dark:text-white">
                  {formatValue(cohortData.mailerShortDescription)}
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Right Column - Sidebar for Metadata (narrower, 30-40% space) */}
      <div className="space-y-4">
        <Card className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-md">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base text-gray-900 dark:text-white">
              <Hash className="h-4 w-4 text-green-500 dark:text-green-400" />
              Creation Filters
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm">
            {cohortData.filters_structured &&
            Object.keys(cohortData.filters_structured).length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {Object.entries(cohortData.filters_structured).map(
                  ([key, value]) => {
                    const { label, displayValue } = humanizeFilterLabel(
                      key,
                      value,
                    );
                    return (
                      <Badge key={key} variant="secondary" className="text-xs">
                        {displayValue ? `${label}: ${displayValue}` : label}
                      </Badge>
                    );
                  },
                )}
              </div>
            ) : (
              <p className="text-gray-500 dark:text-gray-400">
                No specific filters applied
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderMailingLedgerContent = (
    mailingData: PpcFarmMailingLedgerData,
  ) => (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-4">
      {/* Left Column - Main Information Panel (wider, 60-70% space) */}
      <div className="lg:col-span-2 space-y-4">
        {/* Mailing Information */}
        <Card className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-md">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base text-gray-900 dark:text-white">
              <Mail className="h-4 w-4 text-blue-500 dark:text-blue-400" />
              Mailing Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">
                Date Entered:
              </span>
              <span className="text-gray-900 dark:text-white">
                {formatDate(mailingData.dateEntered)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">
                Date Mailed:
              </span>
              <span className="text-gray-900 dark:text-white">
                {(() => {
                  // Apply conditional date logic same as table
                  if (mailingData.dateMailed) {
                    // Has actual Date Mailed value
                    return formatDate(mailingData.dateMailed);
                  }

                  // No Date Mailed value - apply business logic
                  if (mailingData.dateEntered) {
                    // Handle both Firestore Timestamp and plain date objects
                    let enteredDate: Date;
                    try {
                      enteredDate = (mailingData.dateEntered as any).toDate
                        ? (mailingData.dateEntered as any).toDate()
                        : new Date(mailingData.dateEntered as any);
                    } catch (error) {
                      enteredDate = new Date(mailingData.dateEntered as any);
                    }
                    const enteredYear = enteredDate.getFullYear();

                    if (enteredYear <= 2023) {
                      // Use Date Entered as fallback for 2023 and earlier with label
                      return (
                        <span>
                          {formatDate(mailingData.dateEntered)}{" "}
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            (Entered)
                          </span>
                        </span>
                      );
                    } else {
                      // Show "Pending" for 2024 and later
                      return (
                        <Badge
                          variant="secondary"
                          className="bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300"
                        >
                          Pending
                        </Badge>
                      );
                    }
                  }

                  return "—";
                })()}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">
                Addressing Type:
              </span>
              <span className="text-gray-900 dark:text-white">
                {formatValue(mailingData.addressingType)}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Mailer Details */}
        <Card className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-md">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base text-gray-900 dark:text-white">
              <FileText className="h-4 w-4 text-purple-500 dark:text-purple-400" />
              Mailer Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {mailingData.mailerHeadline && (
              <div>
                <span className="text-gray-600 dark:text-gray-400 block">
                  Headline:
                </span>
                <span className="text-gray-900 dark:text-white">
                  {formatValue(mailingData.mailerHeadline)}
                </span>
              </div>
            )}
            {mailingData.customUrl && (
              <div>
                <span className="text-gray-600 dark:text-gray-400 block">
                  Custom URL:
                </span>
                <span className="text-gray-900 dark:text-white text-xs font-mono bg-gray-100 dark:bg-gray-700 p-1 rounded">
                  {formatValue(mailingData.customUrl)}
                </span>
              </div>
            )}
            {mailingData.notes && (
              <div>
                <span className="text-gray-600 dark:text-gray-400 block">
                  Notes:
                </span>
                <span className="text-gray-900 dark:text-white">
                  {formatValue(mailingData.notes)}
                </span>
              </div>
            )}
            {mailingData.linkToMailer && (
              <div className="pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled
                  className="flex items-center gap-2 opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-300 dark:border-gray-600"
                >
                  <ExternalLink className="h-4 w-4" />
                  View Mailer
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Right Column - Sidebar for Associated Data (narrower, 30-40% space) */}
      <div className="space-y-4">
        {/* Cohort Information */}
        <Card className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-md">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base text-gray-900 dark:text-white">
              <Users className="h-4 w-4 text-green-500 dark:text-green-400" />
              Cohort Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Cohort:</span>
              {mailingData.cohort ? (
                <Badge
                  variant="secondary"
                  className="bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300 text-xs"
                >
                  {mailingData.cohort}
                </Badge>
              ) : (
                <span className="text-gray-500">—</span>
              )}
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">
                Cohort Quantity:
              </span>
              <span className="text-gray-900 dark:text-white">
                {formatNumber(mailingData.cohortQuantity)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">
                Drop Number:
              </span>
              <span className="text-gray-900 dark:text-white">
                {formatNumber(mailingData.dropNumber)}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Include in Dashboard Status */}
        <Card className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-md">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base text-gray-900 dark:text-white">
              <Hash className="h-4 w-4 text-indigo-500 dark:text-indigo-400" />
              Dashboard Status
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm">
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-400">
                Include in Dashboard:
              </span>
              {formatBoolean(mailingData.includeInMailingCostDashboard)}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (ledgerType) {
      case "target":
        return renderTargetLedgerContent(data as PpcFarmTargetLedgerData);
      case "cohort":
        return renderCohortLedgerContent(data as PpcFarmCohortLedgerData);
      case "mailing":
        return renderMailingLedgerContent(data as PpcFarmMailingLedgerData);
      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl">
        <DialogHeader className="pb-3 border-b border-gray-200 dark:border-gray-700">
          <DialogTitle className="flex items-center gap-2 text-lg text-gray-900 dark:text-white">
            <Target className="h-5 w-5 text-yellow-500 dark:text-yellow-400" />
            {getModalTitle()}
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-600 dark:text-gray-400">
            {getModalDescription()}
          </DialogDescription>
        </DialogHeader>

        {renderContent()}
      </DialogContent>
    </Dialog>
  );
};

export default GenericLedgerDetailModal;
